/**
 * Path selection for the R0002 Toast data fetch.
 *
 * The Lambda-backed path replaces ~149 serial GraphQL round trips with one call.
 * It is now the DEFAULT — measured 90s to 8s on production, 2026-08-27. The legacy
 * browser-side path stays in the tree through the bake period as the rollback
 * target and as the automatic fallback when the Lambda fails.
 *
 * Resolution order, cheapest first:
 *   1. ?r0002ToastPath=legacy|lambda|compare   per session, no deploy, no reload
 *   2. localStorage['rd.r0002.toastPath']      sticky per browser
 *   3. REACT_APP_R0002_TOAST_PATH              build override
 *   4. LAMBDA                                  code default
 *
 * Tier 1 is the one that works while on the phone with a customer. Tiers 1 and 2
 * are per-user; changing the build override is a rebuild and deploy of the SPA.
 *
 * TO ROLL BACK EVERY USER: set REACT_APP_R0002_TOAST_PATH=legacy and deploy. That
 * is why tier 3 still exists now that the code default has flipped — without it a
 * rollback would mean reverting this file.
 *
 * @module clients/grm/views/R0002/ToastData/toastPath
 */

/** @enum {string} */
export const TOAST_PATH = {
  /** Browser-side pagination + transform. The pre-existing behaviour. */
  LEGACY: 'legacy',
  /** Single call to grmR0002ToastResolver. */
  LAMBDA: 'lambda',
  /** Run both, diff them, publish on window.__r0002ToastDiff. Dev only. */
  COMPARE: 'compare'
}

const STORAGE_KEY = 'rd.r0002.toastPath'

const VALID = new Set(Object.values(TOAST_PATH))

/**
 * Read the override from the query string, if present and valid.
 *
 * @returns {string|null}
 */
function fromUrl() {
  try {
    const value = new URLSearchParams(window.location.search).get('r0002ToastPath')
    return VALID.has(value) ? value : null
  } catch (err) {
    return null
  }
}

/**
 * Read the override from localStorage, if present and valid.
 *
 * Wrapped because localStorage throws outright in some privacy modes rather
 * than returning null.
 *
 * @returns {string|null}
 */
function fromStorage() {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY)
    return VALID.has(value) ? value : null
  } catch (err) {
    return null
  }
}

/**
 * Read the build-time override, falling back to the code default.
 *
 * Returns LAMBDA when the variable is unset or holds an unrecognised value. The
 * repo ships no .env, so an unset variable is the normal case and the code
 * default is what actually governs production.
 *
 * @returns {string}
 */
function fromEnv() {
  const value = process.env.REACT_APP_R0002_TOAST_PATH
  return VALID.has(value) ? value : TOAST_PATH.LAMBDA
}

/**
 * Resolve which Toast fetch path this session should use.
 *
 * @returns {'legacy'|'lambda'|'compare'}
 */
export function getToastPath() {
  return fromUrl() || fromStorage() || fromEnv()
}

/**
 * Persist a path choice for this browser. Exposed for console use during the
 * bake period; pass null to clear.
 *
 * @param {string|null} path
 * @returns {boolean} True if the value was stored or cleared.
 */
export function setToastPath(path) {
  try {
    if (path === null) {
      window.localStorage.removeItem(STORAGE_KEY)
      return true
    }
    if (!VALID.has(path)) return false
    window.localStorage.setItem(STORAGE_KEY, path)
    return true
  } catch (err) {
    return false
  }
}
